/**
 * Entity assignments — who can do manual data entry on which equipment.
 *
 * Backed by entity_assignments (migration 0068). The roster GET is readable by any
 * member; add/remove require model.write (member+, re-enforced server-side). `mine`
 * is the caller's own assigned entities (the mobile "my equipment" home). This is the
 * WHO↔WHAT half of the universal manual-entry model; the manual-entry FIELDS come from
 * my-manual-entry-targets (see ./my-manual-entry).
 */

import { api } from "@/lib/api-client";
import type { Paginated } from "./types";

/** One user assigned to an entity (mirrors twin.Assignment). */
export interface EntityAssignment {
  user_id: string;
  email: string;
  display_name: string;
  role: string;
  created_at: string;
}

/** One of the caller's own assignments (mirrors twin.MyAssignment). */
export interface MyAssignment {
  entity_id: string;
  entity_name: string;
  entity_kind: string;
  role: string;
  created_at: string;
}

export const twinAssignments = {
  /** Users assigned to an entity. `GET /v1/twin/entities/{id}/assignments`. */
  listForEntity: (entityId: string) =>
    api.get<Paginated<EntityAssignment>>(
      `/v1/twin/entities/${encodeURIComponent(entityId)}/assignments`,
    ),
  /** Assign a user (member+). Idempotent on (entity, user). `POST …/assignments`. */
  add: (entityId: string, body: { user_id: string; role?: string }) =>
    api.post<EntityAssignment>(
      `/v1/twin/entities/${encodeURIComponent(entityId)}/assignments`,
      body,
    ),
  /** Unassign a user (member+). `DELETE …/assignments/{user_id}`. */
  remove: (entityId: string, userId: string) =>
    api.delete<void>(
      `/v1/twin/entities/${encodeURIComponent(entityId)}/assignments/${encodeURIComponent(userId)}`,
    ),
  /** The caller's own assigned entities in the active org. `GET /v1/twin/my-assignments`. */
  mine: () => api.get<Paginated<MyAssignment>>(`/v1/twin/my-assignments`),
};
